import { datadogRum } from '@datadog/browser-rum';
import { TableDataAttributes } from '../components/NotificationsList/NotificationsTable/NotificationsTable';
import { Locations } from '../models/locations';
import logger from './logger.service';

const TIMES_STORAGE_KEY = 'init-times';

const logRenderStart = (newInit: $TSFixMe) => {
    try {
        performance.clearMarks('app-render-start');
        performance.mark('app-render-start');

        performance.clearMeasures('measure-app-read');
        performance.measure('measure-app-read', 'app-read-start', 'app-render-start');

        datadogRum.addAction('app start rendering', {
            'new-init': newInit,
            'nav-to-app-read-start': Math.trunc(performance.getEntriesByName('app-read-start')[0]?.startTime),
            'nav-to-render-start': Math.trunc(performance.getEntriesByName('app-render-start')[0]?.startTime),
            'app-read-duration': Math.trunc(performance.getEntriesByName('measure-app-read')[0]?.duration)
        });
    } catch (error) {
        logger.error("can't monitor app start rendering", error, { name: 'render monitoring error' });
    }
};

const saveInitTime = (refreshLoadStartTime: $TSFixMe) => {
    try {
        if (refreshLoadStartTime || !getInitTime()) {
            const times = {
                loadStart: performance?.timing?.navigationStart,
                startRoute: window?.location?.pathname
            };

            sessionStorage.setItem(TIMES_STORAGE_KEY, JSON.stringify(times));
        }
    } catch (error) {}
};

const getInitTime = () => {
    try {
        const data = sessionStorage.getItem(TIMES_STORAGE_KEY);
        if (data) return JSON.parse(data);
    } catch (error) {}

    return null;
};

export const clearInitTime = () => {
    try {
        sessionStorage.removeItem(TIMES_STORAGE_KEY);
    } catch (error) {}
};

const clearPerformanceData = () => {
    try {
        performance.clearMarks();
        performance.clearMeasures();
    } catch (error) {
        logger.error("can't clear monitor measures", error, { name: 'clear monitoring error' });
    }
};

const logInitDuration = () => {
    const initTimes = getInitTime();
    if (initTimes) {
        try {
            const LoadDuration = Date.now().valueOf() - initTimes.loadStart.valueOf();
            performance.measure('measure-read-to-now', 'app-read-start');
            performance.measure('measure-render-to-now', 'app-render-start');

            const readToInitDuration = Math.trunc(performance.getEntriesByName('measure-read-to-now')[0]?.duration);
            const renderDuration = Math.trunc(performance.getEntriesByName('measure-render-to-now')[0]?.duration);
            const readStartTime = Math.trunc(performance.getEntriesByName('app-read-start')[0]?.startTime);
            const FCPTime = Math.trunc(performance.getEntriesByName('first-contentful-paint')[0]?.startTime);
            const performanceResourceTimings = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

            const CDN_URL = 'https://cdn.walkme.com/';
            const itemsToMeasure = performanceResourceTimings.filter(it => it.name.startsWith(CDN_URL) && it.transferSize > 1024 * 100);
            const sum = itemsToMeasure.reduce((acc, it) => {
                return acc + ((it.transferSize / 1024) * 1000) / it.duration;
            }, 0);
            let cdnDownloadSpeed;
            if (sum) {
                cdnDownloadSpeed = `${Math.trunc(sum / itemsToMeasure.length)} kb/s`;
            } else {
                cdnDownloadSpeed = 'unknown';
            }
            const table = document.querySelector(`[${TableDataAttributes.NOTIFICATIONS_LISTS_SIZES}]`);
            const notificationsSizes = JSON.parse(table?.getAttribute(TableDataAttributes.NOTIFICATIONS_LISTS_SIZES) ?? '{}');

            datadogRum.addAction('init time', {
                duration: LoadDuration,
                'app-read-and-render-duration': readToInitDuration,
                'app-page-render-duration': renderDuration,
                'app-read-start-to-FCP': FCPTime - readStartTime,
                startRoute: initTimes.startRoute,
                route: window.location.pathname,
                cdnDownloadSpeed,
                notificationsSizes
            });
        } catch (error) {
            logger.error("can't monitor app init time", error, { name: 'init monitoring error' });
        }

        clearInitTime();
    }

    clearPerformanceData();
};

export const logAuthorizationPerformance = () => {
    try {
        performance.measure('measure-auth-after-read', 'app-read-start', 'authorization-start');
        performance.measure('measure-auth-duration', 'authorization-start', 'authorization-end');
        // performance.measure('measure-get-token-duration', 'get-token-start', 'get-token-end');
        // performance.measure('measure-init-shell-sdk-duration', 'init-shell-sdk-start', 'init-shell-sdk-end');
        performance.measure('measure-get-user-data-duration', 'get-user-data-start', 'get-user-data-end');

        const authorizationStartTime = Math.trunc(performance.getEntriesByName('authorization-start')[0]?.startTime);
        const authorizationTimeAfterReadStart = Math.trunc(performance.getEntriesByName('measure-auth-after-read')[0]?.duration);
        const authorizationDuration = Math.trunc(performance.getEntriesByName('measure-auth-duration')[0]?.duration);
        // const getTokenDuration = Math.trunc(performance.getEntriesByName('measure-get-token-duration')[0]?.duration);
        // const initShellSDKDuration = Math.trunc(performance.getEntriesByName('measure-init-shell-sdk-duration')[0]?.duration);
        const initClientSDKDuration = Math.trunc(performance.getEntriesByName('measure-init-client-sdk-duration')[0]?.duration);
        const getLoggedInUserDuration = Math.trunc(performance.getEntriesByName('measure-get-user-data-duration')[0]?.duration);

        datadogRum.addAction('authorization complete', {
            'authorization-start': authorizationStartTime,
            'authorization-after-read-start': authorizationTimeAfterReadStart,
            'authorization-duration': authorizationDuration,
            // 'get-token-duration': getTokenDuration,
            // 'init-shell-sdk-duration': initShellSDKDuration,
            'init-client-sdk-duration': initClientSDKDuration,
            'get-logged-in-user-duration': getLoggedInUserDuration
        });
    } catch (error) {
        logger.error("can't monitor app's authorization times", error, { name: 'init monitoring error' });
    }
};

let didLogInitDuration = false
const checkMutation = (mutationsList: $TSFixMe, observer: MutationObserver) => {
    try {
        for (const mutation of mutationsList) {
            //todo: check also in non-workstation dialog or required end users dialog
            if (mutation.type === 'childList') {
                let page;

                if (document.querySelector('[data-testid=non-workstation-system]')) {
                    page = true;
                } else if (window.location.pathname === Locations.Edit) {
                    page = document.querySelector('[data-testid=edit-notification-page]');
                } else {
                    // table route (default on unknown)
                    page = document.querySelector('[table-data-loaded=true]') || document.querySelector('[data-testid=empty-content]');
                }

                if (page) {
                    if (!didLogInitDuration) {
                      didLogInitDuration = true
                      logInitDuration();
                    }
                    window.sendLoadEndEvent?.()
                    window.sendViewEndEvent?.()
                    observer.disconnect()
                    break;
                }
            }
        }
    } catch (error) {
        logger.error('error in check page mutation', error, { name: 'monitoring error' });
    }
};

export const subscribeToPageLoad = () => {
    try {
        const observer = new MutationObserver(checkMutation);

        const config = { childList: true, subtree: true };

        const app = document.getElementById('root');

        app && observer.observe(app, config);
    } catch (error) {
        logger.error('error in subscription to mutation observer', error, { name: 'mutation subscription error' });
    }
};

const isContinuesLoad = () => {
    try {
        const urlParams = new URLSearchParams(window.location.search);

        //if load after authentication redirect, there will be a 'code' parameter
        return !!urlParams.get('code');
    } catch (error) {
        logger.error("can't get urlParams", error, { name: 'get monitoring params error' });
        return false;
    }
};

export const monitorPageLoadTime = () => {
    try {
        if (!performance || !performance.getEntriesByName || !performance.mark || !performance.measure || !performance.timing.navigationStart) {
            logger.log("browser doesn't support performance monitoring ", { name: 'monitoring problem' }, 'warn');
            return;
        }

        logRenderStart(!isContinuesLoad());
        saveInitTime(!isContinuesLoad());
        subscribeToPageLoad();
    } catch (error) {
        logger.error("can't monitoring app ", error, { name: 'monitoring problem' });
    }
};
